// Contraction Timer Types

// Database types (matching Supabase schema)
export interface Contraction {
  id: string;
  pregnancy_id: string;
  started_by: string;
  start_time: string;
  end_time: string | null;
  duration_seconds: number | null;
  interval_seconds: number | null;
  created_at: string;
}

export interface ContractionEmergencyEvent {
  id: string;
  pregnancy_id: string;
  reported_by: string;
  event_type: EmergencyEventType;
  event_time: string;
  notes: string | null;
  created_at: string;
}

export type EmergencyEventType = 'water_break' | 'bleeding';

export interface ContractionSettings {
  id: string;
  user_id: string;
  pregnancy_id: string;
  hospital_rule: HospitalRule;
  notification_mode: NotificationMode;
  stats_update_interval: StatsUpdateInterval;
  created_at: string;
  updated_at: string;
}

// Hospital rules
export type HospitalRule = '5-1-1' | '4-1-1' | '3-1-1';

export interface HospitalRuleConfig {
  name: HospitalRule;
  label: string;
  description: string;
  intervalMinutes: number;
  durationMinutes: number;
  hours: number;
}

export const HOSPITAL_RULES: HospitalRuleConfig[] = [
  {
    name: '5-1-1',
    label: '5-1-1 (Standard)',
    description: 'Wehen alle 5 Min, mind. 1 Min lang, seit 1 Stunde',
    intervalMinutes: 5,
    durationMinutes: 1,
    hours: 1,
  },
  {
    name: '4-1-1',
    label: '4-1-1 (Früher)',
    description: 'Wehen alle 4 Min, mind. 1 Min lang, seit 1 Stunde',
    intervalMinutes: 4,
    durationMinutes: 1,
    hours: 1,
  },
  {
    name: '3-1-1',
    label: '3-1-1 (Spätschwangerschaft)',
    description: 'Wehen alle 3 Min, mind. 1 Min lang, seit 1 Stunde',
    intervalMinutes: 3,
    durationMinutes: 1,
    hours: 1,
  },
];

// Notification modes
export type NotificationMode = 'off' | 'important' | 'all';

export interface NotificationModeConfig {
  value: NotificationMode;
  label: string;
  description: string;
}

export const NOTIFICATION_MODES: NotificationModeConfig[] = [
  {
    value: 'off',
    label: 'Aus',
    description: 'Keine Benachrichtigungen',
  },
  {
    value: 'important',
    label: 'Wichtiges',
    description: 'Tracking gestartet, Krankenhaus-Regel erfüllt, Notfälle',
  },
  {
    value: 'all',
    label: 'Alles',
    description: 'Jede Wehe löst eine Benachrichtigung aus',
  },
];

// Stats update interval (in minutes)
export type StatsUpdateInterval = 30 | 60 | null;

export const STATS_UPDATE_INTERVALS: { value: StatsUpdateInterval; label: string }[] = [
  { value: null, label: 'Nie' },
  { value: 30, label: 'Alle 30 Min' },
  { value: 60, label: 'Alle 60 Min' },
];

// Phases
export type ContractionPhase = 'early_latent' | 'late_latent' | 'active' | 'transition';

export interface PhaseConfig {
  phase: ContractionPhase;
  label: string;
  color: string;
  bgColor: string;
  progressPercent: number;
  hint: string;
}

export const PHASES: PhaseConfig[] = [
  {
    phase: 'early_latent',
    label: 'Frühe Latenzphase',
    color: 'text-blue-600',
    bgColor: 'bg-blue-500',
    progressPercent: 25,
    hint: 'Noch Zeit, entspann dich',
  },
  {
    phase: 'late_latent',
    label: 'Späte Latenzphase',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-500',
    progressPercent: 50,
    hint: 'Beobachte die Entwicklung',
  },
  {
    phase: 'active',
    label: 'Aktive Phase',
    color: 'text-orange-600',
    bgColor: 'bg-orange-500',
    progressPercent: 75,
    hint: 'Krankenhaus-Check!',
  },
  {
    phase: 'transition',
    label: 'Übergangsphase',
    color: 'text-red-600',
    bgColor: 'bg-red-500',
    progressPercent: 95,
    hint: 'Zeit fürs Krankenhaus!',
  },
];

// Stats
export interface ContractionStats {
  count: number;
  avgDurationSeconds: number | null;
  avgIntervalSeconds: number | null;
  trend: ContractionTrend;
  isRegular: boolean;
  phase: ContractionPhase;
}

export type ContractionTrend = 'decreasing' | 'stable' | 'increasing';

// Emergency triggers
export interface EmergencyTrigger {
  type: 'long_contraction' | 'short_intervals';
  message: string;
  severity: 'warning' | 'critical';
}

// Timer state
export interface TimerState {
  isActive: boolean;
  currentContractionId: string | null;
  startTime: Date | null;
  elapsedSeconds: number;
  timeSinceLastContraction: number;
}

// Premium feature flags (for future premium gating)
export interface PremiumFeatures {
  unlimitedHistory: boolean;
  partnerNotifications: boolean;
  smartRecommendations: boolean;
  detailedStats: boolean;
}

export const DEFAULT_PREMIUM_FEATURES: PremiumFeatures = {
  unlimitedHistory: true, // Currently all features are enabled
  partnerNotifications: true,
  smartRecommendations: true,
  detailedStats: true,
};
